import axios from "axios";

import store from "../store";
// import { API_BASE_URL } from "../config/env.config";
import { handleLogin, handleLogout } from "../store/authentication";
import {
    API_VERSION,
    AUTH_SERVICE_DOMAIN,
    AUTH_SERVICE_PATH,
} from "../config/axios";
import { getUserData } from "./utils";
import { setUserDataOnLocalStorage } from "./authHelper";
import { getAuthUserDetails } from "../services/user.service";
import {
    isSettingsUrl,
    settingsHandler,
    isSettingsChanged,
} from "./settings.utils";
import { showGlobalToast, markErrorAsHandled } from "../hooks/ToastContext";

const defaultBaseURL = "/api";
let permissionTimestamp = false;

class Request {
    constructor() {
        this.isRefreshing = false;
        this.failedRequests = [];
        this.isCallingSettings = false;
        this.client = axios.create({
            baseURL: defaultBaseURL,
            timeout: 60000,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
        });
        this.beforeRequest = this.beforeRequest.bind(this);
        this.onRequestSuccess = this.onRequestSuccess.bind(this);
        this.onRequestFailure = this.onRequestFailure.bind(this);
        this.processQueue = this.processQueue.bind(this);
        this.client.interceptors.request.use(this.beforeRequest);
        this.client.interceptors.response.use(
            this.onRequestSuccess,
            this.onRequestFailure
        );
    }

    beforeRequest(config) {
        const accessToken = store.getState().auth.accessToken;
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    }

    async onRequestSuccess(response) {
        const headers = response.headers;
        if (headers["x-permission-timestamp"]) {
            const userPermissionTimestamp = localStorage.getItem(
                "user-permission-timestamp"
            );
            if (
                !permissionTimestamp &&
                response?.config?.url !== `${API_VERSION}/user` &&
                userPermissionTimestamp &&
                headers["x-permission-timestamp"] > userPermissionTimestamp
            ) {
                permissionTimestamp = true;
                localStorage.setItem(
                    "user-permission-timestamp",
                    headers["x-permission-timestamp"]
                );
                const userDetails = await getAuthUserDetails();
                const prevTokens = JSON.parse(
                    localStorage.getItem("userData")
                )?.token;

                const newUserDetails = {
                    ...userDetails?.data?.data,
                    token: prevTokens,
                };

                setUserDataOnLocalStorage(newUserDetails);
                permissionTimestamp = false;
                location.reload();
            }
        }
        const settingsHash = headers?.["x-settings-timestamp"] || null;
        if (
            settingsHash &&
            !isSettingsUrl(response?.config?.url) &&
            isSettingsChanged(settingsHash) &&
            !this.isCallingSettings
        ) {
            this.isCallingSettings = true;
            settingsHandler()
                .then(() => {
                    this.isCallingSettings = false;
                })
                .catch((error) => {});
        }
        return response;
    }
    /**
     * Determine whether to handle the error globally.
     * Only allow global handling for 429.
     */
    shouldHandleGlobally(error) {
        const { response } = error;

        // Allow global handling only for 429
        if (response?.status === 429) {
            return true;
        }

        // Exclude all other status codes from global handling
        return false;
    }

    async onRequestFailure(err) {
        const { response } = err;
        if (!this.shouldHandleGlobally(err)) {
            if (
                response?.status === 401 &&
                err &&
                err.config &&
                !err.config.__isRetryRequest
            ) {
                if (this.isRefreshing) {
                    try {
                        const token = await new Promise((resolve, reject) => {
                            this.failedRequests.push({ resolve, reject });
                        });
                        err.config.headers.Authorization = `Bearer ${token}`;
                        return this.client(err.config);
                    } catch (e) {
                        store.dispatch(handleLogout());
                        setTimeout(() => {
                            window.location.href = "/login";
                        }, 100);
                        reject(err.response);
                        return e;
                    }
                }
                this.isRefreshing = true;
                err.config.__isRetryRequest = true;
                return new Promise((resolve, reject) => {
                    const currentUser = getUserData();
                    this.client
                        .post(
                            `${API_VERSION}/${AUTH_SERVICE_PATH}/${AUTH_SERVICE_DOMAIN}/auth/refresh`,
                            { refresh_token: currentUser.token?.refreshToken }
                        )
                        .then(async (_response) => {
                            err.config.headers.Authorization = `Bearer ${_response.data.data?.token?.refreshToken}`;
                            this.isRefreshing = false;
                            currentUser.token = _response.data.data.token;
                            store.dispatch(handleLogin(currentUser));
                            this.processQueue(
                                null,
                                _response.data.data.token?.accessToken
                            );
                            resolve(this.client(err.config));
                        })
                        .catch((e) => {
                            store.dispatch(handleLogout());
                            setTimeout(() => {
                                window.location.href = "/login";
                            }, 100);
                            reject(err.response);
                        });
                });
            }
        } else {
            if (response?.status === 429) {
                // console.log(
                //     "🚀 ~ Request ~ onRequestFailure ~ response:",
                //     response
                // );
                // markErrorAsHandled(err);
                // showGlobalToast("error", {
                //     detail: "You've made too many requests in a short period of time. Please try again after some time",
                //     life: 3000,
                // });
                // return Promise.reject(err);
            }
        }
        throw response;
    }

    processQueue(error, token = null) {
        this.failedRequests.forEach((prom) => {
            if (error) {
                prom.reject(error);
            } else {
                prom.resolve(token);
            }
        });
        this.failedRequests = [];
    }
}

const request = new Request();

export default request.client;
